import React, { useEffect, useState } from 'react';
import Page from '../../../components-ui/page';
import Datatable from '../../../components-ui/data-table/mui/DatatableGroup';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BSKT_DotHoc } from '../../../services/dot_hoc/DotHocModels';
import * as DotHocReducer from '../../../faetures/dot-hoc/dotHocSlide';
import {
    faEye,
    faPenAlt,
    faPlusCircle,
    faTrashAlt,
    faSearch,
    faArrowRotateForward
} from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import CreateOrEditDotHocModal from './CreateOrEditDotHocModal';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import Button from '../../../components-ui/button/Button';
import { formatDateVN } from '../../../utils/convertDate';
import SearchInput from '../../../components-ui/input/search-input';

const DotHocPage = () => {
    const {
        createOrEditModel,
        isShowConfirmDeleteModal,
        isShowCreateOrEditModal,
        listData,
        selectedId,
        status
    } = useAppSelector((x) => x.dotHoc);
    const dispatch = useDispatch();

    // ===== SEARCH STATE =====
    const [filteredList, setFilteredList] = useState<BSKT_DotHoc[]>(listData ?? []);
    const [resetSearchCounter, setResetSearchCounter] = useState(0);

    // Đồng bộ khi listData thay đổi
    useEffect(() => {
        setFilteredList(listData ?? []);
    }, [listData]);

    const onSearchDotHoc = (value: string) => {
        const keyword = value.trim().toLowerCase();
        if (!keyword) {
            setFilteredList(listData ?? []);
            return;
        }

        const result = (listData ?? []).filter((item: BSKT_DotHoc) => {
            const fields = [
                item.Ten_dot,
                item.Dot?.toString(),
                item.Nam_tuyen_sinh?.toString(),
                item.Ghi_chu
            ];
            return fields.some((f) => (f || '').toLowerCase().includes(keyword));
        });

        setFilteredList(result);
    };

    const tong = filteredList.length;

    return (
        <Page title="Đợt học">
            <div className="content">
                <div className="container-fluid">
                    {/* HEADER */}
                    <div className="ts-page-header mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="ts-page-icon rounded-circle d-flex align-items-center justify-content-center">
                                🗓️
                            </div>
                            <div>
                                <h4 className="mb-1">DANH SÁCH ĐỢT HỌC</h4>
                                <div className="text-muted small">
                                    Quản lý các đợt học / đợt tuyển sinh theo năm, thời gian bắt đầu và kết thúc.
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className="small-box bg-info mb-0">
                                <div className="inner">
                                    <h4 className="mb-0">{tong}</h4>
                                    <p className="mb-0">Số đợt học</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TOOLBAR: SEARCH + THÊM MỚI */}
                    <div className="card mb-3 shadow-sm">
                        <div className="card-body py-2">
                            <div
                                className="d-flex flex-wrap align-items-center justify-content-between"
                                style={{ gap: 12 }}
                            >
                                {/* Search */}
                                <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                                    <SearchInput
                                        startIcon={<FontAwesomeIcon icon={faSearch} />}
                                        size="small"
                                        sx={{ width: '260px' }}
                                        onSearch={onSearchDotHoc}
                                        autoSearch={true}
                                        resetSignal={resetSearchCounter}
                                        placeholder="Tìm tên đợt, năm, ghi chú..."
                                    />

                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faArrowRotateForward} />}
                                        onClick={() => {
                                            setFilteredList(listData ?? []);
                                            setResetSearchCounter((prev) => prev + 1);
                                        }}
                                    >
                                    </Button>
                                </div>

                                {/* Thêm mới */}
                                <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                                    <Button
                                        startIcon={<FontAwesomeIcon icon={faPlusCircle} />}
                                        onClick={() => {
                                            dispatch(
                                                DotHocReducer.setCreateOrEditModel({
                                                    ID_dot_hoc: 0,
                                                    Dot: 0,
                                                    Nam_tuyen_sinh: 0,
                                                    Ten_dot: '',
                                                    Tu_ngay: '',
                                                    Den_ngay: '',
                                                    Ghi_chu: ''
                                                })
                                            );
                                            dispatch(DotHocReducer.setShowCreateOrEditModal(true));
                                        }}
                                    >
                                        Thêm mới
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BẢNG DANH SÁCH ĐỢT HỌC */}
                    <Box>
                        <Datatable
                            showPagination={true}
                            showCheckBox={false}
                            data={filteredList}
                            columns={[
                                {
                                    caption: 'STT',
                                    align: 'center',
                                    width: 60,
                                    renderCell: (_row: any, index?: number) => (
                                        <Typography>{(index ?? 0) + 1}</Typography>
                                    )
                                },
                                {
                                    caption: 'Tên đợt',
                                    dataField: 'Ten_dot',
                                    align: 'left'
                                },
                                {
                                    caption: 'Đợt',
                                    dataField: 'Dot',
                                    align: 'center',
                                    width: 80
                                },
                                {
                                    caption: 'Năm học',
                                    dataField: 'Nam_tuyen_sinh',
                                    align: 'center',
                                    width: 110
                                },
                                {
                                    caption: 'Từ ngày',
                                    dataField: 'Tu_ngay',
                                    align: 'center',
                                    width: 130,
                                    renderCell: (data: BSKT_DotHoc) => (
                                        <Typography>{formatDateVN(data.Tu_ngay)}</Typography>
                                    )
                                },
                                {
                                    caption: 'Đến ngày',
                                    dataField: 'Den_ngay',
                                    align: 'center',
                                    width: 130,
                                    renderCell: (data: BSKT_DotHoc) => (
                                        <Typography>{formatDateVN(data.Den_ngay)}</Typography>
                                    )
                                },
                                {
                                    caption: '#',
                                    width: 120,
                                    align: 'center',
                                    renderCell: (data: BSKT_DotHoc) => (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                            <Tooltip title="Xem chi tiết">
                                                <IconButton
                                                    color="info"
                                                    onClick={() => {
                                                        dispatch(DotHocReducer.setCreateOrEditModel(data));
                                                        dispatch(DotHocReducer.setShowCreateOrEditModal(true));
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} size="xs" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Sửa">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => {
                                                        dispatch(DotHocReducer.setCreateOrEditModel(data));
                                                        dispatch(DotHocReducer.setShowCreateOrEditModal(true));
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faPenAlt} size="xs" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Xóa">
                                                <IconButton
                                                    color="error"
                                                    onClick={() => {
                                                        dispatch(
                                                            DotHocReducer.setShowConfirmDeleteModal(data.ID_dot_hoc)
                                                        );
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrashAlt} size="xs" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )
                                }
                            ]}
                        />
                    </Box>

                    {/* MODAL TẠO / SỬA */}
                    {isShowCreateOrEditModal && (
                        <CreateOrEditDotHocModal
                            onClose={() => {
                                dispatch(DotHocReducer.setShowCreateOrEditModal(false));
                            }}
                        />
                    )}
                </div>
            </div>
        </Page>
    );
};

export default DotHocPage;